import { observable, set } from 'mobx';
import { pascalCaseData } from '~utils/CaseUtils';
import { formatLoyaltyLevel } from '~utils/FormatterUtils';
import { IBenefitsInfo } from '~views/dashboard/loyalty-benefits/ILoyaltyBenefits';
import {
    ILoyaltyBenefitsResponse,
    IOfferDetailsResponse
} from '~api/interfaces/ILoyaltyBenefitsResponse';
import { getSortedOffersInfo, PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';
import { INVALID_INVENTORY_STATUSES } from '~internal/utils/OfferNotificationContext';

class Benefits {
    @observable public benefitsDataFormatted: IBenefitsInfo[] = [];

    static fromJSON(benefitsData: ILoyaltyBenefitsResponse): Benefits {
        const benefitsOfferInfo = new Benefits();
        const { user: {benefits_no_sku: offerData }, currentLoyaltyLevel } = benefitsData;

        const benefitsOfferFields = offerData || [];

        const filteredOfferFields = benefitsOfferFields?.filter(
            (levelDetails) => {
                const criteriaLoyaltyLevels = formatLoyaltyLevel(levelDetails?.criteria as string);

                return currentLoyaltyLevel === '0' ||
                    (criteriaLoyaltyLevels
                        ? criteriaLoyaltyLevels.includes(currentLoyaltyLevel)
                        : true
                    );
            }
        );

        const benefitsDataFormatted = filteredOfferFields?.map(
            (offerDetails: IOfferDetailsResponse) => ({
                inCart: offerDetails?.inCart,
                inventoryStatus: Benefits.getEvaluatedInventoryStatus(offerDetails, true),
                meetsCriteria: offerDetails?.meetsCriteria,
                offerButtonLabel: pascalCaseData(offerDetails?.promo?.text?.action as string, true),
                redeemedStatus: offerDetails?.promo?.text?.redeemed,
                offerCode: offerDetails?.offerCode,
                offerDescription: offerDetails?.promo?.text?.description,
                offerHowToUse: offerDetails?.promo?.text?.howToUse,
                offerImage: offerDetails?.image,
                offerLinkAction: offerDetails?.promo?.URL?.action,
                offerPoints: offerDetails?.points,
                offerTitle: offerDetails?.promo?.text?.title,
                offerUsed: offerDetails?.used,
                offerView: '',
                skuID: '',
                subHeading: offerDetails?.promo?.text?.subHeading,
                benefits: JSON.parse(offerDetails?.benefits || '[]'),
                highestPriorityClass: null,
                criteriaViewValidations: offerDetails?.criteriaViewValidations,
                offerNotificationFields: {
                    criteriaFieldsList: offerDetails?.criteriaFields,
                    criteriaAllowedTierList: offerDetails?.criteriaAllowedTiers,
                    criteriaRequiredSkuList: offerDetails?.criteriaRequiredSkus,
                    pointsNeeded: offerDetails?.pointsNeeded,
                    inventoryStatus: Benefits.getEvaluatedInventoryStatus(offerDetails, false)
                }
            })
        );

        const modifiedData = {
            benefitsDataFormatted
        };

        set(benefitsOfferInfo, modifiedData);

        return benefitsOfferInfo;
    }

    private static getEvaluatedInventoryStatus(offerDetails: IOfferDetailsResponse, toLowerCase: boolean) {
        if (offerDetails?.skuBased && offerDetails.skus?.choicesPcatData?.length > 0) {
            let validInventoryStatus: string | null = null;
            let invalidInventoryStatus: string | null = null;
            let invalidInventoryStatusCounter = 0;
            offerDetails.skus.choicesPcatData.forEach(sku => {
                if (sku.skuData?.inventoryStatus) {
                    if (INVALID_INVENTORY_STATUSES.includes(sku.skuData.inventoryStatus)) {
                        invalidInventoryStatus = toLowerCase ? sku.skuData.inventoryStatus.toLowerCase() : sku.skuData.inventoryStatus;
                        invalidInventoryStatusCounter++;
                    } else {
                        validInventoryStatus = toLowerCase ? sku.skuData.inventoryStatus.toLowerCase() : sku.skuData.inventoryStatus;
                    }
                }
            });

            return offerDetails.skus.choicesPcatData.length > invalidInventoryStatusCounter ? validInventoryStatus : invalidInventoryStatus;
        } else {
            return null;
        }
    }

    public static getSortedBenefitsInfo(benefitsDataFormatted: IBenefitsInfo[], priorityOrder?: PriorityOrderSort[], categoryOrder?: CategoryOrderSort[]) {
        const benefitsOfferInfo = new Benefits();
        const modifiedData = {
            benefitsDataFormatted: getSortedOffersInfo(benefitsDataFormatted, priorityOrder, categoryOrder)
        };
        set(benefitsOfferInfo, modifiedData);

        return benefitsOfferInfo;
    }
}

export default Benefits;
